import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, Paper, TextField, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { LeftMenuDrawer } from '../../navigation/LeftMenuDrawer/LeftMenuDrawer';
import { SearchMenuBar } from '../../navigation/SearchMenuBar';
import { useDriverDirectory } from '../../../hooks/useDriverDirectory/useDriverDirectory';
import { useDirectoryBusinessOptions } from '../../../hooks/useDriverDirectory/useDirectoryBusinessOptions';
import {
  canViewDriverDirectory,
  businessOptionsErrorMessage,
  directoryDefaults,
  directoryErrorMessage,
  formatDirectoryTime,
  validateDirectoryParams,
} from '../../../services/DriverDirectory';
import { useStyles } from './DriverDirectoryPage.styles';

function DirectoryContent() {
  const classes = useStyles();
  const [draft, setDraft] = useState({ ...directoryDefaults });
  const [params, setParams] = useState({ ...directoryDefaults });
  const [validation, setValidation] = useState('');
  const [refreshRequest, setRefreshRequest] = useState(0);
  const options = useDirectoryBusinessOptions();
  const [appliedRevision, setAppliedRevision] = useState(null);
  const optionsReady = options.status === 'success' && options.data.length > 0;
  const validBusiness = (id) => id === '' || (optionsReady && options.data.some((item) => String(item.id) === id));
  const enabled = optionsReady && appliedRevision !== null &&
    appliedRevision === options.revision && validBusiness(params.businessId);
  const { data, isFetching, error, refetch } = useDriverDirectory(params, { enabled, revision: options.revision });
  const optionsRef = useRef(options);
  optionsRef.current = options;
  useEffect(() => {
    // Suspend even still-valid scopes until explicitly applied after a reload.
    setAppliedRevision(null);
    const current = optionsRef.current;
    const reconcile = (previous) => ({
      ...previous, page: 1,
      businessId: current.status === 'success' &&
        (!current.data.length || (previous.businessId !== '' &&
          !current.data.some((item) => String(item.id) === previous.businessId)))
        ? 'unselected' : previous.businessId,
    });
    setDraft(reconcile);
    setParams(reconcile);
  }, [options.status, options.revision]);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const handledRefreshRequest = useRef(0);

  // Runs after the page-one query has mounted, never refetching the old page.
  useEffect(() => {
    if (refreshRequest > handledRefreshRequest.current && enabled) {
      handledRefreshRequest.current = refreshRequest;
      const request = refetchRef.current();
      if (request && request.catch) request.catch(() => {});
    }
  }, [refreshRequest, enabled]);

  const updateDraft = (key, value) => {
    setValidation('');
    setDraft((previous) => ({
      ...previous,
      [key]: value,
      ...(key === 'businessId' && value === '' ? { lastClockInDays: '' } : {}),
      ...(key === 'businessId' && value !== '' && value !== 'unselected' && previous.businessId === '' &&
        previous.neverClockedIn !== 'true' ? { lastClockInDays: '7' } : {}),
      ...(key === 'neverClockedIn' && value === 'true' ? { lastClockInDays: '' } : {}),
      ...(key === 'lastClockInDays' && value !== '' && previous.neverClockedIn === 'true'
        ? { neverClockedIn: '' } : {}),
    }));
  };

  const commit = (next) => {
    try {
      if (!optionsReady || !validBusiness(next.businessId)) {
        throw new Error('Select an authorized area, then Apply.');
      }
      validateDirectoryParams(next);
      setValidation('');
      setParams(next);
      setAppliedRevision(options.revision);
      return true;
    } catch (failure) {
      setValidation(failure.message);
      return false;
    }
  };
  const apply = (event) => {
    event.preventDefault();
    commit({ ...draft, sort: params.sort, pageSize: params.pageSize, page: 1 });
  };
  const changeOrdering = (key, value) => {
    const next = { ...params, [key]: value, page: 1 };
    if (commit(next)) setDraft((previous) => ({ ...previous, [key]: value }));
  };
  const restart = () => {
    if (!enabled) return;
    setParams((previous) => ({ ...previous, page: 1 }));
    setRefreshRequest((previous) => previous + 1);
  };
  const reloadAreas = () => {
    setAppliedRevision(null);
    setValidation('');
    options.reload();
  };
  const select = (label, value, onChange, options, disabled = false) => (
    <TextField select label={label} value={value} onChange={onChange}
      variant="outlined" size="small" disabled={disabled}
      SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} inputProps={{ 'aria-label': label }}>
      {options.map(([optionValue, text]) => <option key={optionValue} value={optionValue}>{text}</option>)}
    </TextField>
  );
  const ready = enabled && !isFetching && !error && data;
  const context = ready ? data.filterContext : null;
  const timezone = context && context.businessId != null ? context.businessTimezone : 'UTC';
  const windowDisabled = !optionsReady || !validBusiness(draft.businessId) || !draft.businessId || draft.neverClockedIn === 'true';
  const scopeName = context && (context.businessId == null ? 'All authorized areas' :
    options.data.find((item) => String(item.id) === String(context.businessId))?.name || 'Selected area');

  return (
    <>
      <Box className={classes.introduction}>
        <Typography variant="h5" component="h1" className={classes.title}>Driver directory</Typography>
      </Box>
      <Paper className={classes.panel}>
        <Box component="form" onSubmit={apply}>
          <Box className={classes.filters}>
            {select('Area', optionsReady ? draft.businessId : 'unselected',
              (event) => updateDraft('businessId', event.target.value),
              [['unselected', 'Select an area'], ...(optionsReady
                ? [['', 'All authorized areas'], ...options.data.map(({ id, name }) => [String(id), name])]
                : [])], !optionsReady)}
            {select('Recent period', draft.lastClockInDays, (event) => updateDraft('lastClockInDays', event.target.value),
              [['7', 'Last 7 Days'], ['30', 'Last 30 Days'], ['90', 'Last 90 Days'], ['', 'All history']], windowDisabled)}
            <Box className={classes.actions}>
              <Button type="submit" variant="contained" color="primary" disabled={!optionsReady || isFetching}>Apply</Button>
              <Button onClick={restart} variant="outlined" disabled={!enabled || isFetching}>Refresh</Button>
            </Box>
          </Box>
          <details className={classes.moreFilters}>
            <summary>More filters</summary>
            <Box className={classes.secondaryFilters}>
              {select('Global clock-in state', draft.clockedIn, (event) => updateDraft('clockedIn', event.target.value),
                [['all', 'All states'], ['true', 'Clocked in'], ['false', 'Clocked out']])}
              {select('Recorded history', draft.neverClockedIn, (event) => updateDraft('neverClockedIn', event.target.value),
                [['', 'Any history'], ['false', 'Has clock-ins'], ['true', 'Never clocked in']])}
              {select('Sort', params.sort, (event) => changeOrdering('sort', event.target.value),
                [['last_clock_in_desc', 'Latest clock-in first'], ['last_clock_in_asc', 'Earliest clock-in first'], ['name_asc', 'Name A–Z']], !enabled || isFetching)}
              {select('Page size', params.pageSize, (event) => changeOrdering('pageSize', Number(event.target.value)),
                [[25, '25 per page'], [50, '50 per page'], [100, '100 per page']], !enabled || isFetching)}
              <Button onClick={reloadAreas} disabled={options.status === 'loading'}>Reload areas</Button>
            </Box>
          </details>
          {validation && <Typography className={classes.filterState} role="alert" variant="body2" color="error">{validation}</Typography>}
          {options.status === 'loading' && <Typography className={classes.filterState} role="status">Loading areas…</Typography>}
          {options.status === 'success' && !optionsReady && <Typography className={classes.filterState} role="status">No authorized areas. Contact an administrator.</Typography>}
          {options.status === 'error' && <Box className={classes.filterState} role="alert">
            <Typography variant="body2" color="error">{businessOptionsErrorMessage(options.error)}</Typography>
            <Button onClick={reloadAreas}>Retry areas</Button>
          </Box>}
        </Box>
      </Paper>
      <Paper className={classes.panel}>
        <Box className={classes.toolbar}>
          <Typography variant="h6" component="h2">
            {ready ? `${data.totalResults} drivers` : 'Drivers'}
          </Typography>
        </Box>
        {!enabled && <Box className={classes.state} role="status"><Typography variant="body2">Select an area and Apply to load drivers.</Typography></Box>}
        {enabled && isFetching && <Box role="status" aria-label="Loading driver directory" className={classes.state}>
          {[0, 1, 2, 3].map((index) => <Skeleton key={index} height={56} />)}
          <Typography variant="body2">Loading driver directory…</Typography>
        </Box>}
        {enabled && !isFetching && error && <Box role="alert" className={classes.state}>
          <Typography variant="body2" color="error">{directoryErrorMessage(error)}</Typography>
           <Button onClick={restart} variant="outlined">Retry</Button>
        </Box>}
        {enabled && !isFetching && !error && !data && <Box role="status" className={classes.state}>
          <Typography>No directory response yet.</Typography>
           <Button onClick={restart} variant="outlined">Retry</Button>
        </Box>}
        {ready && <>
          <Box className={classes.metadata}>
            <Typography variant="caption">Membership scope: {scopeName}</Typography>
            <Typography variant="caption">Global last clock-in</Typography>
            <Typography variant="caption">Server timezone: {context.businessTimezone || (context.businessId == null ? 'Not provided · Display: UTC' : 'Unavailable')}</Typography>
            {context.lastClockInDays != null && <Typography variant="caption">{context.lastClockInDays} area calendar days</Typography>}
            <Typography variant="caption">As of {formatDirectoryTime(data.asOfUtc, timezone)}</Typography>
          </Box>
          {data.results.length > 0 ? <table className={classes.table} aria-label="Employed drivers">
            <thead><tr>{['Name', 'Phone', 'Home city/town', 'Last clock-in'].map((heading) => <th key={heading} scope="col">{heading}</th>)}</tr></thead>
            <tbody>{data.results.map((driver) => {
              const name = [driver.firstName, driver.lastName].filter(Boolean).join(' ') || 'Name unavailable';
              const phone = driver.phone?.trim() || '';
              const dialable = /^\+?[\d\s().-]+$/.test(phone) && /^[0-9]{7,15}$/.test(phone.replace(/\D/g, ''));
              const phoneTarget = dialable ? phone.replace(/[^\d+]/g, '') : null;
              return <tr key={driver.id}>
                <td data-label="Name"><span className={classes.name}>{name}</span></td>
                <td data-label="Phone">{dialable ? <a className={classes.phone} href={`tel:${phoneTarget}`} aria-label={`Call ${name} at ${phone}`}>{phone}</a> : <span>{phone || 'Phone unavailable'}</span>}</td>
                <td data-label="Home city/town"><span>{driver.homeAddress?.city?.trim() || 'City unavailable'}</span></td>
                <td data-label="Last clock-in">{driver.lastClockInAtUtc == null && params.neverClockedIn === 'true' ? 'Never clocked in' : formatDirectoryTime(driver.lastClockInAtUtc, timezone)}</td>
              </tr>;
            })}</tbody>
          </table> : <Box className={classes.state}>
            <Typography variant="subtitle1">{data.totalResults === 0 ? 'No drivers match these filters' : 'No drivers on this page'}</Typography>
            <Typography variant="body2" className={classes.muted}>{data.totalResults === 0 ? 'Try a longer period or another area.' : 'Refresh to return to page 1.'}</Typography>
            {data.totalResults > 0 && <Button onClick={restart}>Refresh</Button>}
          </Box>}
          <Box className={classes.pagination}>
            <Button aria-label="Previous page" disabled={data.page <= 1} onClick={() => commit({ ...params, page: data.page - 1 })}>Previous</Button>
            <Typography variant="body2">Page {data.page} · {data.totalPages} total pages · {data.results.length} of {data.totalResults} shown</Typography>
            <Button aria-label="Next page" disabled={data.page >= data.totalPages} onClick={() => commit({ ...params, page: data.page + 1 })}>Next</Button>
          </Box>
        </>}
      </Paper>
    </>
  );
}

export function DriverDirectoryPage() {
  const classes = useStyles();
  const user = useSelector((state) => state.user.user);
  return (
    <main className={classes.content} aria-label="driver directory page">
      <LeftMenuDrawer />
      <Box className={classes.header}><SearchMenuBar title="Driver Directory" /></Box>
      <Box className={classes.body}>
        {user?.id != null && canViewDriverDirectory(user) ? <DirectoryContent key={user.id} /> : <Paper className={classes.panel}>
          <Typography variant="h5" component="h1">Driver directory access required</Typography>
          <Typography role="alert" variant="body2">
            This directory is available only to Delivery Operations Portal Admins, Local Area Managers and Regional Area Managers.
          </Typography>
        </Paper>}
      </Box>
    </main>
  );
}